import * as Cesium from "cesium";

import { getEntityBBox } from "./Utility";
import { disableCameraControls, restoreCameraControls } from "./MapUtility";

export const handleSelectingForComment = (viewer, originalCameraControls, callback) => {
  let startPoint, currentMousePosition;
  let isDrawing = false;
  const RECTANGLE_ID = "drawn-for-commet";
  const RECTANGLE2_ID = "drawnborder-for-commet";

  const handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);

  // LEFT_DOWN: Start drawing or check for entity selection
  // LEFT_DOWN: Start drawing or check for entity selection
  handler.setInputAction((click) => {
    // Try to pick a top-most object under the cursor
    const pickedObject = viewer.scene.pick(click.position);
    // Ray-cast to globe to get a 3D cartesian point (for rectangle drawing)
    let cartesian;
    try {
      cartesian = viewer.scene.pickPosition(click.position);
    } catch {
      cartesian = undefined;
    }

    if (!Cesium.defined(cartesian)) return; // or just skip setting startPoint

    startPoint = cartesian;

    // Case 1: If an entity was clicked (and we are not already drawing), call the callback with entity info
    if (pickedObject && pickedObject.id && !isDrawing) {
      const entity = pickedObject.id;

      // Robustly read "id" from GeoJSON properties (falls back to entity.id)
      const time = viewer.clock?.currentTime;
      const id =
        entity.properties?.id &&
        (typeof entity.properties.id.getValue === "function"
          ? entity.properties.id.getValue(time) // handle if it's a Cesium Property
          : entity.properties.id);
      // Correct way to reach the DataSource that owns this entity
      const ds = entity.entityCollection && entity.entityCollection.owner;
      // Prefer custom dataSource.layerName, otherwise fall back to dataSource.name
      const layerName = (ds && (ds.layerName ?? ds.name)) ?? null;

      callback({
        type: "entity",
        id,
        layerName,
        bbox: getEntityBBox(entity),
      });
      return;
    }

    // Case 2: Start drawing rectangle if we have a valid cartesian
    if (!cartesian) return;
    if (isDrawing) return;
    isDrawing = true;

    // Remove existing rectangle/outline entities if present
    const existingEntity = viewer.entities.getById(RECTANGLE_ID);
    if (existingEntity) viewer.entities.remove(existingEntity);
    const existingEntity2 = viewer.entities.getById(RECTANGLE2_ID);
    if (existingEntity2) viewer.entities.remove(existingEntity2);

    disableCameraControls(viewer, originalCameraControls);

    // Draw rectangle fill (dynamic while dragging)
    viewer.entities.add({
      id: RECTANGLE_ID,
      rectangle: {
        coordinates: new Cesium.CallbackProperty(() => {
          const endPoint = viewer.scene.pickPosition(currentMousePosition);
          if (!endPoint) return null;

          const carto1 = Cesium.Cartographic.fromCartesian(startPoint);
          const carto2 = Cesium.Cartographic.fromCartesian(endPoint);

          let west = Math.min(carto1.longitude, carto2.longitude);
          let south = Math.min(carto1.latitude, carto2.latitude);
          let east = Math.max(carto1.longitude, carto2.longitude);
          let north = Math.max(carto1.latitude, carto2.latitude);

          // --- Fix: normalize rectangle ---
          const EPS = 1e-12; // tiny epsilon in radians
          if (east - west < EPS) east = west + EPS; // ensure east > west
          if (north - south < EPS) north = south + EPS; // ensure north > south

          return Cesium.Rectangle.fromRadians(west, south, east, north);
        }, false),
        material: Cesium.Color.BLUE.withAlpha(0.08),
        outline: true,
        outlineColor: Cesium.Color.RED,
      },
    });

    // Draw dashed border (polyline) around the dynamic rectangle
    viewer.entities.add({
      id: RECTANGLE2_ID,
      polyline: {
        positions: new Cesium.CallbackProperty(() => {
          const endPoint = viewer.scene.pickPosition(currentMousePosition);
          if (!endPoint) return [];

          const c1 = Cesium.Cartographic.fromCartesian(startPoint);
          const c2 = Cesium.Cartographic.fromCartesian(endPoint);

          const west = Math.min(c1.longitude, c2.longitude);
          const south = Math.min(c1.latitude, c2.latitude);
          const east = Math.max(c1.longitude, c2.longitude);
          const north = Math.max(c1.latitude, c2.latitude);

          return Cesium.Cartesian3.fromRadiansArray([west, south, east, south, east, north, west, north, west, south]);
        }, false),
        width: 1,
        material: new Cesium.PolylineDashMaterialProperty({
          color: Cesium.Color.RED,
          gapColor: Cesium.Color.TRANSPARENT,
          dashLength: 12,
        }),
        clampToGround: false,
      },
    });
  }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

  // MOUSE_MOVE: update current mouse position while drawing
  handler.setInputAction((movement) => {
    currentMousePosition = movement.endPosition;
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  // LEFT_UP: Finalize rectangle selection
  handler.setInputAction(() => {
    if (!isDrawing || !startPoint || !currentMousePosition) return;

    const endPoint = viewer.scene.pickPosition(currentMousePosition);
    if (!endPoint) return;

    const carto1 = Cesium.Cartographic.fromCartesian(startPoint);
    const carto2 = Cesium.Cartographic.fromCartesian(endPoint);

    // Compute bounds in degrees
    const bounds = {
      west: Cesium.Math.toDegrees(Math.min(carto1.longitude, carto2.longitude)),
      south: Cesium.Math.toDegrees(Math.min(carto1.latitude, carto2.latitude)),
      east: Cesium.Math.toDegrees(Math.max(carto1.longitude, carto2.longitude)),
      north: Cesium.Math.toDegrees(Math.max(carto1.latitude, carto2.latitude)),
    };

    // Freeze rectangle entity
    const rectangleEntity = viewer.entities.getById(RECTANGLE_ID);
    if (rectangleEntity) {
      let { west, south, east, north } = bounds;

      const EPS = 1e-8; // tiny epsilon in degrees
      if (east < west) [west, east] = [east, west]; // swap if reversed
      if (north < south) [south, north] = [north, south]; // swap if reversed
      if (east - west < EPS) east = west + EPS; // ensure east > west
      if (north - south < EPS) north = south + EPS; // ensure north > south

      rectangleEntity.rectangle.coordinates = Cesium.Rectangle.fromDegrees(west, south, east, north);
      rectangleEntity.rectangle.material = Cesium.Color.TRANSPARENT;
      rectangleEntity.rectangle.outline = true;
      rectangleEntity.rectangle.outlineColor = Cesium.Color.YELLOW;
    }

    // Freeze outline entity
    const outlineEntity = viewer.entities.getById(RECTANGLE2_ID);
    if (outlineEntity) {
      outlineEntity.polyline.positions = Cesium.Cartesian3.fromDegreesArray([
        bounds.west,
        bounds.south,
        bounds.east,
        bounds.south,
        bounds.east,
        bounds.north,
        bounds.west,
        bounds.north,
        bounds.west,
        bounds.south,
      ]);
      outlineEntity.polyline.width = 1;
      outlineEntity.polyline.material = new Cesium.PolylineDashMaterialProperty({
        color: Cesium.Color.RED,
        gapColor: Cesium.Color.TRANSPARENT,
        dashLength: 12,
      });
      outlineEntity.polyline.clampToGround = false;
    }

    // Send callback with rectangle selection
    callback({
      type: "rectangle",
      bounds,
    });

    restoreCameraControls(viewer, originalCameraControls);
    isDrawing = false;
  }, Cesium.ScreenSpaceEventType.LEFT_UP);

  return handler;
};

export const deleteDrawingForComment = (viewer) => {
  // Early exit if viewer or entities collection is not available
  const entities = viewer?.entities;
  if (!entities || typeof entities.getById !== "function" || typeof entities.remove !== "function") {
    // Nothing to do; avoid throwing by returning silently
    return;
  }

  // IDs of the entities to remove
  const RECTANGLE_ID = "drawn-for-commet";
  const RECTANGLE2_ID = "drawnborder-for-commet";

  // Try to get the first entity; remove only if it exists
  const oldRectangle = entities.getById?.(RECTANGLE_ID); // ?. protects if getById isn't a function
  if (oldRectangle) {
    entities.remove?.(oldRectangle); // ?. protects if remove isn't a function
  }

  // Try to get the second entity; remove only if it exists
  const oldOutline = entities.getById?.(RECTANGLE2_ID);
  if (oldOutline) {
    entities.remove?.(oldOutline);
  }
};
